import { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { translate } from "react-switch-lang";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import Chart from "react-google-charts";
import Select from "react-select";

import { useHttpClient } from "../../../../security/hooks/axiosProvider";
import { RootState } from "../../../../store";
import { StatisticsData } from "../../../../models/statisticsDataDto";
import { establishment, nationalitiesStatistics, occupations, sexes } from "../../../../constant";
import { Nationality } from "../../../../models/nationalityDto";
import Breadcrumbs from "../../../../shared/breadcrumb/breadCrumb";
import styles from "./trainingPathStatistics.module.scss";

interface Props {
  t: (key: string) => string;
}

const TrainingPathStatistics: React.FC<Props> = ({ t }) => {
  var randomColor = require("randomcolor");
  const httpClient = useHttpClient(true);
  const currentLang = useSelector((state: RootState) => state.language.currentLang);
  const [registeredUsers, setRegisteredUsers] = useState<number>(0);
  const [selectedUsersChart, setSelectedUsersChart] = useState<string>("signup_nationality");
  const [selectedYear, setSelectedYear] = useState<Date>(new Date());
  const [selectedTrainingPathsChart, setSelectedTrainingPathsChart] = useState<string>("training_path");
  const [nbrUsersByNbrTrainingPaths, setNbrUsersByNbrTrainingPaths] = useState<Array<StatisticsData>>([]);
  const [nbrUsersByCompletedTrainingPaths, setNbrUsersByCompletedTrainingPaths] = useState<Array<StatisticsData>>([]);
  const [averageTimeSpentByUsersToCompleteATrainingPath, setTheAverageTimeSpentByUsersToCompleteATrainingPath] = useState<Array<Array<string | number>>>([]);
  const [usersChartData, setUsersChartData] = useState<Array<Array<string | number>>>([]);
  const [usersIndicatorsData, setUsersIndicatorsData] = useState<Map<string, Array<StatisticsData>>>(new Map<string, Array<StatisticsData>>());
  const [registeredUsersByMonth, setRegisteredUsersByMonth] = useState<Map<string, Array<StatisticsData>>>(new Map<string, Array<StatisticsData>>());
  const [trainingPathsChartData, setTrainingPathsChartData] = useState<Array<Array<string | number>>>([]);
  const [registeredUsersByMonthChartData, setRegisteredUsersByMonthChartData] = useState<Array<Array<string | number>>>([]);
  const [trainingPathsIndicatorsData, setTrainingPathsIndicatorsData] = useState<Map<string, Array<StatisticsData>>>(new Map<string, Array<StatisticsData>>());
  const [trainingPathByDownloadNumberData, setTrainingPathByDownloadNumberData] = useState([]);

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "green" : "white",
    }),
  };

  const USERS_PROFILES_OPTIONS = [
    { value: "signup_nationality", label: t("signup_nationality") },
    { value: "age", label: t("age") },
    { value: "signup_genre", label: t("signup_genre") },
    { value: "signup_occupation", label: t("signup_occupation") },
    { value: "establishment", label: t("establishment") },
  ];

  const TRAINING_PATH_OPTIONS = [
    { value: "training_path", label: t("training_path") },
  ];


  useEffect(() => {
    getTrainingPathByDownloadNumber();
    // eslint-disable-next-line
  }, [currentLang]);

  useEffect(() => {
    getNumberOfRegisteredUsers();
    getNbrUsersByNbrTrainingPaths();
    getUsersIndicators();
    getNbrRegisteredUsersByMonth();
    // eslint-disable-next-line
  }, [selectedYear, currentLang]);

  useEffect(() => {
    getNbrUsersByCompletedTrainingPaths();
    getTrainingPathsIndicators();
    getTheAverageTimeSpentByUsersToCompleteATrainingPath();
    // eslint-disable-next-line
  }, [currentLang, selectedYear]);

  const getTrainingPathByDownloadNumber = (): void => {
    httpClient.current?.get<Array<StatisticsData>>(`/statistics/nbrDownloadByTrainingPath` ,{ params: { language: currentLang.toUpperCase()} })
    .then(({ data }) => {
      const trainingPathData = [
        ['label', 'value'],
        ...data.map(item => [item.label, item.value])
      ];
      setTrainingPathByDownloadNumberData(trainingPathData);

    })
  };


  const getNumberOfRegisteredUsers = (): void => {
    httpClient.current?.get<number>(`/statistics/registeredUsers`, { params: { year: selectedYear.getFullYear() } }).then(({ data }) => setRegisteredUsers(data));
  };

  const getNbrUsersByNbrTrainingPaths = (): void => {
    httpClient.current?.get<Array<StatisticsData>>(`/statistics/nbrUsersByNbrTrainingPaths`, { params: { language: currentLang.toUpperCase() ,year: selectedYear.getFullYear() } }).then(({ data }) => setNbrUsersByNbrTrainingPaths(data)
    );
  };

  const getNbrUsersByCompletedTrainingPaths = (): void => {
    httpClient.current
      ?.get<Array<StatisticsData>>(`/statistics/nbrUsersByCompletedTrainingPaths`, { params: { language: currentLang.toUpperCase(), year: selectedYear.getFullYear() } })
      .then(({ data }) => setNbrUsersByCompletedTrainingPaths(data));
  };

  const getTheAverageTimeSpentByUsersToCompleteATrainingPath = (): void => {
    httpClient.current
      ?.get<Array<StatisticsData>>(`/statistics/averageTimeSpentByUsersToCompleteATrainingPath`, { params: { language: currentLang.toUpperCase(), year: selectedYear.getFullYear() } })
      .then(({ data }) => setTheAverageTimeSpentByUsersToCompleteATrainingPath(fillBarChartData(data)));
  };

  const getUsersIndicators = (): void => {
    httpClient.current?.get<Map<string, Array<StatisticsData>>>(`/statistics/usersIndicators`, { params: { year: selectedYear.getFullYear() } }).then(({ data }) => {
      setUsersIndicatorsData(data);
      setUsersChartData(data["signup_nationality"].map(({ label, value }) => chartTranslator("signup_nationality", label, value)));
    });
  }; 

  const getTrainingPathsIndicators = (): void => {
    httpClient.current
      ?.get<Map<string, Array<StatisticsData>>>(`/statistics/trainingPathsIndicators`, { params: { language: currentLang.toUpperCase(), year: selectedYear.getFullYear() } })
      .then(({ data }) => {
        setTrainingPathsIndicatorsData(data);
        setTrainingPathsChartData(data["training_path"].map(({ label, value }) => [label, value]));
      });
  };

  const getNbrRegisteredUsersByMonth = (): void => {
    httpClient.current?.get<Map<string, Array<StatisticsData>>>(`/statistics/registeredUsersByMonth`, { params: { year: selectedYear.getFullYear() } }).then(({ data }) => {
      setRegisteredUsersByMonth(data);
      setRegisteredUsersByMonthChartData(fillBarChartDataWithMonths(data["all"]));
    });
  };

  const ranges = [
    { min: 10, max: 20, label: "10-20" },
    { min: 21, max: 30, label: "21-30" },
    { min: 31, max: 40, label: "31-40" },
    { min: 41, max: 50, label: "41-50" },
    { min: 51, max: 60, label: "51-60" },
    { min: 61, max: 70, label: "61-70" },
    { min: 71, max: 80, label: "71-80" },
    { min: 81, max: 90, label: "81-90" },
    { min: 91, max: 100, label: "91-100" }
  ];
  
  const getIndexFromAgeInterval = (value: number, array: Array<StatisticsData>): number => {
    for (let range of ranges) {
      if (value > range.min - 1 && value < range.max + 1) {
        return array.findIndex(element => element.label === range.label);
      }
    }
    return -1;
  };


  const onChangeTrainingPathsIndicators = ({ value }): void => {
    const type = value;
    setSelectedTrainingPathsChart(value);
    setTrainingPathsChartData(trainingPathsIndicatorsData[value].map(({ label, value }) => chartTranslator(type, label, value)));
  };
 
  


  const handleOnChangeNationality = ({ value }: Nationality) => {
    let filtredBarChartDataByNationality;
    if (registeredUsersByMonth["by_nationality"]) {
      filtredBarChartDataByNationality = registeredUsersByMonth["by_nationality"].filter(element => element.label === value);
    } else {
      // Gérez le cas où registeredUsersByMonth["by_nationality"] est undefined
      filtredBarChartDataByNationality = [];
    }
    
    setRegisteredUsersByMonthChartData(fillBarChartDataWithMonths(filtredBarChartDataByNationality));
  };

  


  const fillBarChartDataWithMonths = (data: Array<StatisticsData>): Array<Array<string | number>> => {
    for (let i = 1; i < 13; i++) if (!data.some(element => element.key === i)) data.push({ label: null, key: i, value: 0 });
    return data.map(({ key, value }) => [key, value]);
  };

  const fillBarChartData = (data: Array<StatisticsData>): Array<Array<string | number>> => {
    return data.map(({ label, value }) => [label, value, randomColor(), value]);
  };

 const handleOnChangeUsersProfilesIndicators = ({ value }) => {
    const type = value;
    setSelectedUsersChart(type);
    if (type === "age") {
      let customAgeArray: StatisticsData[] = [
        { label: "10-20", value: 0 },
        { label: "21-30", value: 0 },
        { label: "31-40", value: 0 },
        { label: "41-50", value: 0 },
        { label: "51-60", value: 0 },
        { label: "61-70", value: 0 },
        { label: "71-80", value: 0 },
        { label: "81-90", value: 0 },
        { label: "91-100", value: 0 },
      ];
      usersIndicatorsData[type].forEach(async ({ value }) => {
        const index = getIndexFromAgeInterval(value, customAgeArray);
        if (index > -1) customAgeArray[index].value = ++customAgeArray[index].value;
      });
      setUsersChartData(customAgeArray.map(({ label, value }) => [label, value]));
    } else {
      setUsersChartData(usersIndicatorsData[type].map(({ label, value }) => chartTranslator(type, label, value)));
    }
  }; 

  const chartTranslator = (type: string, label: string, value): Array<Array<string | number>> => {
    if (type === "signup_nationality" || type === "registered_users_by_nationality") {
      switch (currentLang) {
        case "fr":
          return [nationalitiesStatistics.find(nat => nat.value === label)?.fr_label|| label, value];
        case "en":
          return [nationalitiesStatistics.find(nat => nat.value === label)?.en_label|| label, value];
        default:
          return [label, value];
      }
    } else if (type === "establishment") {
      switch (currentLang) {
        case "fr":
          return [establishment.find(nat => nat.value === label)?.fr_label|| label, value];
        case "en":
          return [establishment.find(nat => nat.value === label)?.en_label || label, value];
        default:
          return [label, value];
      }
    } else if (type === "signup_occupation") {
      switch (currentLang) {
        case "fr":
          return [(occupations.find(nat => nat.value === label)?.fr_label) || label, value];
        case "en":
          return [(occupations.find(nat => nat.value === label)?.en_label) || label, value];
        default:
          return [label, value];
      }
    } else if (type === "signup_genre") {
      switch (currentLang) {
        case "fr":
          return [(sexes.find(nat => nat.value === label)?.fr_label) || label, value];
        case "en":
          return [(sexes.find(nat => nat.value === label)?.fr_label) || label, value];
        default:
          return [label, value];
      }
    } else {
      return [label, value];
    }
  };
 

  return (
    <>
      <Breadcrumbs parent={t("training_path_list")} title={t("statistics")} />
      <Container fluid>
        <div className={styles["statistics-year-filter"]}>
          <span className={styles["statistics-year-filter-label"]}>{t("year-filter")}</span>
          <div className={styles["statistics-year-filter-input"]}>
            <DatePicker className="form-control" selected={selectedYear} dateFormat="yyyy" showYearPicker onChange={(date: Date) => setSelectedYear(date)} />
          </div>
        </div>

        <div className={styles["first-section-statistics"]}>
          <Card className={`${styles["card-registered-wrapper"]} ${styles["registered"]}`}>
            <CardBody className={styles["synthesis-registered-card-body"]}>
              <div className={styles["synthesis-registered-wrapper"]}>
                <div className={styles["synthesis-registered-details"]}>
                  <div className={`${styles["icon"]} ${styles["primary"]}`}>
                    <i className="icofont icofont-users"></i>
                  </div>
                  <div className={styles["description"]}>
                    <h6>{`${registeredUsers}`}</h6>
                  </div>
                  <div className={styles["registered-users-description"]}>
                    <span>{`${t("registered_users")}`}</span>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <div className={styles["nested-first-section-statistics"]}>
            <Card className={styles["card-wrapper"]}>
              <CardBody className={styles["synthesis-card-body"]}>
                <div className={styles["synthesis-wrapper"]}>
                  <div className={styles["synthesis-details"]}>
                    <div className={`${styles["icon"]} ${styles["secondary"]}`}>
                      <i className="icofont icofont-ebook"></i>
                    </div>
                    <div className={styles["second-description"]}>
                      <span className={styles["value"]}>{nbrUsersByNbrTrainingPaths.find(e => e.key === 1)?.value || 0}</span>
                      <span>{' '}{t("1user_start_course")}</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card className={styles["card-wrapper"]}>
              <CardBody className={styles["synthesis-card-body"]}>
                <div className={styles["synthesis-wrapper"]}>
                  <div className={styles["synthesis-details"]}>
                    <div className={`${styles["icon"]} ${styles["secondary"]}`}>
                      <i className="icofont icofont-ebook"></i>
                    </div>
                    <div className={styles["second-description"]}>
                      <span className={styles["value"]}>{nbrUsersByNbrTrainingPaths.find(e => e.key === 2)?.value || 0}</span>
                      <span>{' '}{t("2user_start_course")}</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card className={styles["card-wrapper"]}>
              <CardBody className={styles["synthesis-card-body"]}>
                <div className={styles["synthesis-wrapper"]}>
                  <div className={styles["synthesis-details"]}>
                    <div className={`${styles["icon"]} ${styles["secondary"]}`}>
                      <i className="icofont icofont-ebook"></i>
                    </div>
                    <div className={styles["second-description"]}>
                      <span className={styles["value"]}>{nbrUsersByNbrTrainingPaths.find(e => e.key === 3)?.value || 0}</span>
                      <span>{' '}{t("3user_start_course")}</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card className={styles["card-wrapper"]}>
              <CardBody className={styles["synthesis-card-body"]}>
                <div className={styles["synthesis-wrapper"]}>
                  <div className={styles["synthesis-details"]}>
                    <div className={`${styles["icon"]} ${styles["secondary"]}`}>
                      <i className="icofont icofont-ebook"></i>
                    </div>
                    <div className={styles["second-description"]}>
                      <span className={styles["value"]}>{nbrUsersByNbrTrainingPaths.find(e => e.key === 4)?.value || 0}</span>
                      <span>{' '}{t("4user_start_course")}</span>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            {nbrUsersByCompletedTrainingPaths.map(({ label, value }, index) => (
              <Card className={styles["card-wrapper"]} key={index}>
                <CardBody className={styles["synthesis-card-body"]}>
                  <div className={styles["synthesis-wrapper"]}>
                    <div className={styles["synthesis-details"]}>
                      <div className={`${styles["icon"]} ${styles["thirth"]}`}>
                        <i className="icofont icofont-ebook"></i>
                      </div>
                      <div className={styles["second-description"]}>
                        <span className={styles["value"]}>{value}</span>
                        <span>{` ${t("users_complete")} `}</span>
                        <span className={styles["label"]}>{label}</span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
        <div className={styles["chart-wrapper"]}>
          <Row>
            <Col ms={12}>
              <Card>
                <CardHeader>
                  <div className={styles["barchart-header"]}>
                    <h5>{t("registered_users")}</h5>
                    <Select
                      defaultValue={nationalitiesStatistics[0]}
                      className={styles["barchart-select"]}
                      styles={customStyles}
                      options={nationalitiesStatistics}
                      getOptionLabel={option => option[`${currentLang}_label`]}
                      onChange={handleOnChangeNationality}
                    />
                  </div>
                </CardHeader>
                <CardBody>
                  <Chart
                    width={"100%"}
                    height={"500px"}
                    chartType="Bar"
                    loader={<div>Loading Chart</div>}
                    data={[[ t("months"), t("registered_users")], ...registeredUsersByMonthChartData]}
                    options={{
                      legend: { position: "none" },
                      chart: {
                        title: t("registered_users"),
                      },
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xl={6} lg={12} md={12} sm={12}>
              <Card>
                <CardHeader>
                  <h5>{t("user_profiles")}</h5>
                </CardHeader>
                <CardBody>
                  <div className={styles["pieChart-header"]}>
                    <span>Filtrer par </span>
                    <Select
                      defaultValue={USERS_PROFILES_OPTIONS[0]}
                      className={styles["pieChart-select"]}
                      options={USERS_PROFILES_OPTIONS}
                      onChange={handleOnChangeUsersProfilesIndicators}
                      styles={customStyles}
                    />
                  </div>
                  <Chart
                    width={"100%"}
                    height={"400px"}
                    chartType="PieChart"
                    loader={<div>Loading Chart</div>}
                    data={[[selectedUsersChart, "Utilisateurs"], ...usersChartData]}
                    options={{
                      title: t(selectedUsersChart),
                      colors: ["#109619", "#dc3812", "#3266cc", "#fe9900", "#226433", "#51bb25", "#f8d62b", "#c83e2c"],
                    }}
                  />
                </CardBody>
              </Card>
            </Col>

            <Col xl={6} lg={12} md={12} sm={12}>
              <Card>
                <CardHeader>
                  <h5>{t("training_path")}</h5>
                </CardHeader>
                <CardBody>
                  <div className={styles["pieChart-header"]}>
                    <span>Filtrer par </span>
                    <Select
                      defaultValue={TRAINING_PATH_OPTIONS[0]}
                      className={styles["pieChart-select"]}
                      options={TRAINING_PATH_OPTIONS}
                      onChange={onChangeTrainingPathsIndicators}
                      styles={customStyles}
                    />
                  </div>
                  <Chart
                    width={"100%"}
                    height={"400px"}
                    chartType="PieChart"
                    loader={<div>Loading Chart</div>}
                    data={[[selectedTrainingPathsChart, "Utilisateurs"], ...trainingPathsChartData]}
                    options={{
                      title: t(selectedTrainingPathsChart),
                      colors: ["#109619", "#dc3812", "#3266cc", "#fe9900", "#226433", "#51bb25", "#f8d62b", "#c83e2c"],
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

        { <Row>
            <Col xl={6} lg={12} md={12} sm={12}>
              <Card>
                <CardHeader>
                  <h5>{t("training_path_by_download_number")}</h5>
                </CardHeader>
                <CardBody>
                  <Chart
                    width={"100%"}
                    height={"400px"}
                    chartType="PieChart"
                    loader={<div>Loading Chart</div>}
                    data={trainingPathByDownloadNumberData}
                    options={{
                      colors: ["#109619", "#dc3812", "#3266cc", "#fe9900", "#226433", "#51bb25", "#f8d62b", "#c83e2c"],
                      pieSliceText: "value",
                      pieSliceTextStyle: {
                        fontSize: 20,
                        bold: true
                      }
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>} 

          <Row>
            <Col ms={12}>
              <Card>
                <CardHeader>
                  <div className={styles["barchart-header"]}>
                    <h5>{t("averageTime_spent_by_users_to_complete_trainingPath")}</h5>
                  </div>
                </CardHeader>
                <CardBody>
                  {averageTimeSpentByUsersToCompleteATrainingPath.length !== 0 ? (
                    <Chart
                      width={"100%"}
                      height={"500px"}
                      chartType="BarChart"
                      loader={<div>Loading Chart</div>}
                      data={[
                        [
                          "Element",
                          "Le temps moyen",
                          { role: "style" },
                          {
                            sourceColumn: 1,
                            role: "annotation",
                            type: "string",
                            calc: "stringify",
                          },
                        ],
                        ...averageTimeSpentByUsersToCompleteATrainingPath,
                      ]}
                      options={{
                        title: t("averageTime_spent_by_users_to_complete_trainingPath_by_Day"),
                        height: 400,
                        bar: { groupWidth: "95%" },
                        backgroundColor: "transparent",
                        legend: { position: "none" },
                      }}
                    />
                  ) : (
                    <div className={styles["no-data-message"]}>no data</div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>
    </>
  );
};

export default translate(TrainingPathStatistics);
